import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { listAttachmentDescriptions } from "@synthesis/shared-services/external/application/client";
import { getRequestQueryKey, getRequestQueryOptions } from "@synthesis/shared-services/external/application/hooks";
import type { RequestDetailResponse } from "@synthesis/shared-services/external/application/types";
import { getFileMetas1 as getFileMetas } from "@synthesis/shared-services/files/external/client";

import { applyCreateApplicationDraftToPageStore, usePageStore } from "../create-application-page.lib";
import { useApplicationDraftId } from "./use-create-application-draft-id";

export const useInitDraft = () => {
  const { draftId } = useApplicationDraftId();
  const pageStore = usePageStore();
  const queryClient = useQueryClient();
  const initializedDraftIdRef = useRef<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(() => Boolean(draftId));

  useEffect(() => {
    if (!draftId || !pageStore) {
      initializedDraftIdRef.current = null;
      return;
    }

    if (initializedDraftIdRef.current === draftId) {
      return;
    }

    let isCancelled = false;
    setIsInitializing(true);

    const loadDraft = async () => {
      try {
        let draft = queryClient.getQueryData<RequestDetailResponse>(getRequestQueryKey({ path: { id: draftId } }));

        if (!draft) {
          draft = await queryClient.fetchQuery(getRequestQueryOptions({ path: { id: draftId } }));
        }

        let attachments: Array<{ description?: string | null; fileId?: string | null }> = [];
        let fileMetasById: Record<string, { baseName: string; ext?: string | null }> = {};

        try {
          attachments = (await listAttachmentDescriptions({ path: { id: draftId } })).data;
          const fileIds = attachments.flatMap((attachment) => (attachment.fileId ? [attachment.fileId] : []));

          if (fileIds.length > 0) {
            const fileMetas = (await getFileMetas({ body: { ids: fileIds } })).data;
            fileMetasById = Object.fromEntries(fileMetas.map((fileMeta) => [fileMeta.id, fileMeta]));
          }
        } catch {
          // Fall back to document names stored in additionalData.
        }

        if (!isCancelled) {
          applyCreateApplicationDraftToPageStore(pageStore, draft, { attachments, fileMetasById });
          initializedDraftIdRef.current = draftId;
        }
      } catch {
        if (!isCancelled) {
          initializedDraftIdRef.current = null;
        }
      } finally {
        if (!isCancelled) {
          setIsInitializing(false);
        }
      }
    };

    void loadDraft();

    return () => {
      isCancelled = true;
    };
  }, [draftId, pageStore, queryClient]);

  return { isInitializing: Boolean(draftId) && isInitializing };
};
